import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { DialogContextProvider } from './DialogContextProvider';
import styles from './Dialog.module.scss';

export type DialogChildren = (args: {
  close: () => void;
  open: () => void;
  opened: boolean;
}) => ReactNode;

interface Props {
  children: DialogChildren;
  root: string;
  type: 'modal' | 'popup';
  pages?: ReactElement;
}

export function Dialog(props: Props) {
  const ref = useRef<HTMLDialogElement | null>(null);
  const [opened, setOpened] = useState<boolean>(false);

  const close = useCallback(() => {
    if (ref.current) {
      ref.current.close();
      setOpened(false);
    }
  }, []);

  const open = useCallback(() => {
    if (ref.current) {
      if (props.type === 'popup') {
        ref.current.show();
      } else {
        ref.current.showModal();
      }
      setOpened(true);
    }
  }, [props.type]);

  useEffect(() => {
    ref.current?.addEventListener('close', () => {
      close();
    });
  }, [close]);

  const dialogClassName = [
    props.type === 'modal' && styles['dialog-modal'],
    props.type === 'popup' && styles['dialog-popup'],
  ]
    .filter(Boolean)
    .join(' ');

  const dialogPagerClassName = [styles['dialog-pager']]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {props.children({ close, open, opened: false })}
      <dialog ref={ref} className={dialogClassName}>
        <DialogContextProvider
          close={close}
          dialogRef={ref}
          opened={opened}
          rootPage={props.root}
        >
          <div className={dialogPagerClassName}>{props.pages}</div>
        </DialogContextProvider>
      </dialog>
    </>
  );
}
